import {
  type CorruptionTier,
  type ExoticPrice,
  exoticFabricationTotal,
  type ModifierSource,
  type ProvidesLine,
  type ResourcePrice,
  type SquadronOption,
  type StatCell,
  type TaggedModifier,
  type Unit,
  type UnitCloak,
  type UnitSquadrons,
  type UnitWeapon,
} from "@sins2-atlas/game-data";
import {
  type CostInput,
  computedCell,
  deriveUnitStats,
  effectiveHp,
  foldStat,
  foldStatCell,
  type Mount,
  type StatModifier,
  type SupplyInput,
  type VerificationState,
  type WeaponInput,
  weaponBombingDamage,
  weaponCooldown,
  weaponDamage,
  weaponDps,
  weaponNovaDamage,
  weaponRange,
} from "./stat-cells";

// The page-side fold (ADR 0009: stats are added up on the page from tagged bonus lists).
// A dataset Unit carries its Base cells and, per stat and per weapon quantity, the list of
// every bonus that can touch it tagged with its source (a Research Subject, a Domain Tier
// unlock, a unit-local unlock, a Corruption Tier, a Ship Item). This module selects the ACTIVE sources
// and folds them with the same compute functions the extractor folds Base with, so every
// surface — the Ships and Structures tables and their sort, the entity page, the comparison
// and its best-per-row marks, the weapon table — reads one join. Base is the empty set,
// Fully-Teched the whole Race's set (every non-corruption source a unit's lists carry IS its
// Race's: applicability was resolved at extraction), a Tech Set (Issue 24) a Set of research
// source keys; the active Corruption Tier rides beside whichever research selection is on.
// The extractor's own check (tech-set-check.ts) folds through this same module.

/** A research source's identity as the string a Tech Set is a set of (CONTEXT.md: Tech
 * Set): `subject:<id>` for a Research Subject, `tier:<domain>:<n>` for a Domain Tier unlock
 * (0-based, the dataset's own index). A unit- or weapon-local `unlock` rides the ONE subject
 * that gates it — ticking that subject ticks the unlock (the closure rule), so its key IS the
 * subject's. */
export function researchSourceKey(
  source: Exclude<
    ModifierSource,
    { kind: "corruption" } | { kind: "item" } | { kind: "level" } | { kind: "ability" }
  >,
) {
  switch (source.kind) {
    case "subject":
      return `subject:${source.id}`;
    case "unlock":
      return `subject:${source.subjectId}`;
    case "tier":
      return `tier:${source.domain}:${source.tier}`;
  }
}

/** Which research sources are on: none (Base), all (Fully-Teched — every research source
 * the unit's lists carry), or a Set of researchSourceKey strings (a Tech Set). */
export type ResearchSelection = "none" | "all" | ReadonlySet<string>;

/** The active sources — the fold's one selector: the research selection and, orthogonal to
 * it, the active Corruption Tier (absent = uncorrupted, ADR 0007) and the Loadout (absent =
 * nothing equipped, ADR 0012).
 *
 * The Loadout is a MAP, not a set: an item's modifier entries are published one per item
 * level, so the level in force selects which of them folds. That level is not a fourth
 * axis — the game levels items by research alone (`research_prerequisites_per_level`), so
 * the Tech Set decides it and the caller resolves it before folding. */
export interface ActiveSources {
  research: ResearchSelection;
  corruption?: CorruptionTier;
  /** The Loadout: equipped Ship Item id → what the fold knows about it. */
  items?: ReadonlyMap<string, EquippedItem>;
  /** The hull's experience Level, 1 to 10 (CONTEXT.md: Level; ADR 0012, Issue 49). Absent
   * IS level 1 — the Base frame — so every existing caller folds unchanged.
   *
   * It is the one lever that is not a source: a Level replaces the base rows (hull, armor,
   * shields, Armor Strength, antimatter, the hangar's capacity) before any modifier runs,
   * and only THEN do its own weapon scalars fold as ordinary `level`-tagged sources. A hull
   * with no ladder ignores it entirely, publishing neither a level table nor a level
   * source. */
  level?: number;
  /** The hull's abilities at the level in force (Issue 51): ability id → ability level,
   * holding exactly the abilities the reader's selection has unlocked. Derived from the
   * unit's own levers rather than chosen — from `levelPrerequisites` on a research-levelled
   * ability, and from `points` below on a level-driven one — so it is never an axis of its
   * own: foldUnit fills it in from the unit it is folding, and `activeAbilityLevels` is the
   * one rule that decides it. */
  abilities?: ReadonlyMap<string, number>;
  /** The Ability Points the reader has spent on this hull (CONTEXT.md: Ability Points;
   * ADR 0015): ability id → the Ability Level BOUGHT, an ability with no entry being
   * unspent. Absent IS nothing spent, which is the Base frame, so every caller that knows
   * nothing about the lever folds exactly what it folded before it existed.
   *
   * It is the third per-hull lever, and it is what a level-driven ability's Ability Level
   * comes from. A hull's Level only gates it: the game sells Ability Levels for Ability
   * Points, and a level-2 Vulkoras Desolator with nothing spent bombs at the bare figure
   * (observed in game 2026-09-09, ADR 0015). The map is keyed by ability id rather than
   * carried positionally because the fold sees one ability at a time; the URL's positional
   * grammar is resolved against the hull's own ability order before it gets here. */
  points?: ReadonlyMap<string, number>;
}

/** What the fold needs to know about one ability: its id, and whichever lever decides its
 * level — the hull level each of its levels requires (`level_source: internal_level`), or
 * the Research Subjects each of its levels needs (`research_prerequisites_per_level`). Never
 * both: the game states exactly one source per ability and extraction fails if it states
 * neither consistently. A `Pick` of the dataset's UnitAbility, so a test can hand in a bare
 * one. */
export interface FoldableAbility {
  id: string;
  requiredUnitLevels?: readonly number[];
  levelPrerequisites?: readonly (readonly string[])[];
}

/** Where the reader's selection leaves one ability (ADR 0015: Ability Points decide a
 * level-driven ability's Ability Level, and the hull's Level only gates it; Issue 54:
 * research decides the other kind).
 *
 *  - `in_force` — the ability is unlocked and this is the Ability Level in force.
 *  - `unspent` — the hull's Level permits the ability and no Ability Point is in it, so it
 *    does nothing and folds nothing (ADR 0015 decision 5; CONTEXT.md: Ability Level —
 *    "Unspent" is our word, the game names the button rather than the state). Dimmed on the
 *    page exactly as a locked one is: undimmed would read as an ability the ship has.
 *  - `locked` — the hull's Level has not reached even the ability's first Ability Level; the
 *    game still lists it, and so does the site, marked with the level it names.
 *  - `research_locked` — the ability's FIRST level is itself gated on research the selection
 *    does not hold, so the hull cannot use it at all. Marked with what it needs, never
 *    hidden — the same treatment `locked` gets, for the same reason.
 *  - `not_level_driven` — the ability names neither lever (an item-granted one, or one the
 *    game gives unconditionally), so nothing the reader picks moves it. */
export type AbilityLevelState =
  | { state: "in_force"; level: number }
  | { state: "unspent" }
  | { state: "locked"; requiredLevel: number }
  | { state: "research_locked"; requiredSubjects: readonly string[] }
  | { state: "not_level_driven" };

/** Where a hull's Level and its allocation jointly leave one level-driven ability
 * (ADR 0015). `requiredUnitLevels[i]` is the hull level Ability Level i+1 needs, and the
 * entries are non-decreasing, so the FIRST entry is the whole of the lock: below it the game
 * does not offer the ability at all. The four shapes the game ships at 2.0.3.0 are [0,2,4],
 * [0,2,4,6,8], [5] and [5,9]; the first two are offered from level 1, the last two are the
 * 33 abilities locked there.
 *
 * `bought` is the Ability Level the reader's Ability Points paid for, zero being unspent —
 * the rule this replaced derived it from `unitLevel` instead, and that model was read in
 * game and failed (ADR 0015). An allocation is always one the game could reach (ADR 0015
 * decision 2), and the boundary the URL and the controls go through is where that promise is
 * kept; here a level past the ability's ladder reads as the ladder's top, so a direct caller
 * can never be told "Ability Level 4 in force" on a three-level ability while the fold, which
 * has no such source, folds nothing (review 2026-09-10). */
export function abilityLevelState(
  ability: FoldableAbility,
  unitLevel: number,
  bought: number,
): AbilityLevelState {
  const required = ability.requiredUnitLevels;
  if (required === undefined) return { state: "not_level_driven" };
  const [first] = required;
  // An empty list cannot lock anything: it states no requirement.
  if (first === undefined) return { state: "not_level_driven" };
  // A locked ability names the level it needs — its FIRST entry, which is the one the reader
  // has not reached. Locked outranks unspent: the reader cannot buy what the game does not
  // offer, and "Requires Level 5" is the fact that says what to change.
  if (first > unitLevel) return { state: "locked", requiredLevel: first };
  if (bought <= 0) return { state: "unspent" };
  return { state: "in_force", level: Math.min(bought, required.length) };
}

/** Which level of a RESEARCH-levelled ability a selection puts in force (Issue 54) — the
 * game's `research_prerequisites_per_level`, where entry i is the flat AND of Research
 * Subjects ability level i+1 needs and an empty entry is an ungated level.
 *
 * Levels unlock in order, so the ladder stops at the first gate the selection does not hold
 * — exactly the rule `itemLevelUnder` holds for a Ship Item's level, and for the same
 * reason. It returns **0** where the first level is itself gated, which an item's ladder
 * never does: 43 of the game's 51 research-levelled abilities gate their first level, so
 * "the hull cannot use this at all yet" is a real answer, not an edge case. */
export function researchAbilityLevel(
  levelPrerequisites: readonly (readonly string[])[],
  research: ResearchSelection,
): number {
  let level = 0;
  for (const gate of levelPrerequisites) {
    if (!gate.every((subjectId) => isSubjectActive(subjectId, research))) break;
    level += 1;
  }
  return level;
}

/** Where the reader's whole selection leaves one ability — the one place the two levers are
 * dispatched between, so the ability list's mark and the fold's map can never disagree about
 * which level is in force. Research decides a `levelPrerequisites` ability (Issue 54); the
 * hull's Level and the reader's Ability Points decide every other (ADR 0015). */
export function abilityLevelStateUnder(
  ability: FoldableAbility,
  active: ActiveSources,
): AbilityLevelState {
  const gates = ability.levelPrerequisites;
  if (gates !== undefined) {
    const level = researchAbilityLevel(gates, active.research);
    if (level > 0) return { state: "in_force", level };
    return { state: "research_locked", requiredSubjects: gates[0] ?? [] };
  }
  return abilityLevelState(ability, activeLevel(active), active.points?.get(ability.id) ?? 0);
}

/** The unlocked abilities at the levels the selection puts in force, under every lever — the
 * map `ActiveSources.abilities` is. A locked one is absent, and so is an UNSPENT one, which
 * is exactly what stops its modifiers folding: nothing is bought until the reader buys it
 * (ADR 0015). */
export function activeAbilityLevels(
  abilities: readonly FoldableAbility[] | undefined,
  active: ActiveSources,
): ReadonlyMap<string, number> {
  const levels = new Map<string, number>();
  for (const ability of abilities ?? []) {
    const state = abilityLevelStateUnder(ability, active);
    if (state.state === "in_force") levels.set(ability.id, state.level);
  }
  return levels;
}

/** The active sources with this hull's ability levels resolved onto them. Called by foldUnit
 * on the unit it is folding, and by the surfaces that fold a battery without its unit
 * (the weapon table), so both read one rule. */
export function withAbilityLevels(
  active: ActiveSources,
  abilities: readonly FoldableAbility[] | undefined,
): ActiveSources {
  return { ...active, abilities: activeAbilityLevels(abilities, active) };
}

/** The Base frame's level: absent means level 1, so a caller that knows nothing about the
 * lever reads exactly what it read before the lever existed. */
export const BASE_LEVEL = 1;

/** The level in force under these sources. */
export function activeLevel(active: ActiveSources): number {
  return active.level ?? BASE_LEVEL;
}

/** One equipped Ship Item, as the fold reads it — the level in force and what it cost.
 *
 * Level and price ride the SAME entry deliberately. They are two facts about one equipped
 * item, and a Loadout carried as two maps could disagree about which items are on: the
 * modifiers would fold from one and the Price from the other, which is the half-applied
 * number this project has fixed twice. Both are required for the same reason — an item with
 * no stated price would add its damage for free. */
export interface EquippedItem {
  /** 1-based, the entry of the item's per-level values in force (ADR 0012: the game levels
   * items by research alone, so the caller resolves this from the Tech Set). */
  level: number;
  /** The item's own credits / metal / crystal (the dataset's `items[].price`). */
  price: ResourcePrice;
  /** The exotics it costs, each carrying its race's fabrication recipe — the same shape
   * and the same per-race price the hull's own `exoticPrice` carries. */
  exoticPrice: readonly ExoticPrice[];
  /** The ability this item gives the hull, where it gives one (the dataset's
   * `items[].ability.id`; 78 of the 192 buyable items at 2.0.3.0, each granting its own).
   * Carried because an
   * item-granted ability is NOT among the hull's own list — the hull's file names it
   * nowhere — so the only way a fold can know the reader has it is the Loadout entry that
   * brought it. Issue 58 needs exactly this: the Zhaltharien Anathema's cloak is granted by
   * its Cloak Generator crest, and no Tech Set can ever switch it on. */
  grantedAbilityId?: string;
}

export const NO_SOURCES: ActiveSources = { research: "none" };

/** Whether one Research Subject is in the selection. Exported because an item's LEVEL is
 * decided by exactly this question (ADR 0012: the game levels items by research alone), and
 * a second hand-written copy of the rule could drift from the fold's. */
export function isSubjectActive(subjectId: string, research: ResearchSelection): boolean {
  if (research === "all") return true;
  if (research === "none") return false;
  return research.has(`subject:${subjectId}`);
}

/** Whether one tagged modifier folds under the active sources. */
export function isSourceActive(source: ModifierSource, active: ActiveSources): boolean {
  if (source.kind === "corruption") return source.tier === active.corruption;
  // A Ship Item's modifier folds when the item is equipped AND the entry belongs to the
  // item level in force. Fully-Teched does NOT equip anything: research is something an
  // empire has, a Loadout something one ship carries (ADR 0012).
  if (source.kind === "item") return active.items?.get(source.id)?.level === source.level;
  // Exactly ONE level's entries fold: the reached level's. The game states each level's value
  // as that level's TOTAL, not as an increment on the level below (levels.ts), so summing the
  // ladder would compound — a level-10 Kol would read +225% weapon damage instead of the +45%
  // ADR 0012 records. This equality is the whole rule.
  if (source.kind === "level") return source.level === activeLevel(active);
  // A passive ability's standing buff folds while the reader's selection has that ability at
  // that Ability Level — the same equality the Level itself folds by, and for the same
  // reason: the game states each ability level's value as that level's TOTAL. A locked or
  // unspent ability is absent from the map, so nothing of it folds (Issue 51, ADR 0015).
  if (source.kind === "ability") return active.abilities?.get(source.id) === source.level;
  const research = active.research;
  if (research === "all") return true;
  if (research === "none") return false;
  return research.has(researchSourceKey(source));
}

function select(list: readonly TaggedModifier[], active: ActiveSources): TaggedModifier[] {
  return list.filter((modifier) => isSourceActive(modifier.source, active));
}

/** The cells the fold returns per battery, one per column of the weapon table: per-shot
 * Damage, Cooldown, per-mount DPS, Nova Damage and Range — and, on a bomb bay only, the
 * planet damage its Damage column shows instead. */
export interface FoldedWeaponCells {
  damage: StatCell;
  cooldown: StatCell;
  dps: StatCell;
  novaDamage: StatCell;
  range: StatCell;
  bombingDamage?: StatCell;
}

/** The stats whose cells the fold decides — exactly the ones a lever can move, in the
 * dataset's own order. Everything else on a unit (supply, build time, durability, the
 * Corruption pool) is a frame-invariant fact: no source touches it, so its Base cell is true
 * under every selection. The hangar and the Provides lines DO move (Issue 48) but are not
 * cells, so they are not in this list — foldSquadrons and activeProvides own them.
 *
 * The three Price cells joined this list with the Loadout (Issue 47, ADR 0012): a Loadout's
 * items are bought, so a loaded hull's Price is the hull plus what it carries. They stay
 * frame-invariant under every OTHER lever — research and Corruption move no price — and a
 * surface with no Loadout (both roster views: `?items=` is declared on the entity route
 * alone) therefore reads exactly the hull's own figures. */
export const FOLDED_STATS = [
  "hull",
  "armor",
  "shields",
  "armorStrength",
  // The Cloak Detection row (Issue 55, ADR 0013). It joined the folded cells with the
  // Loadout and for the Loadout: the Eidolon Cloak Detector Array is the only thing in the
  // game that permanently moves it. It is the one folded cell whose ADR 0003 STATE the
  // selection changes — all six hulls that can buy the item carry no innate detection, so
  // fitting it turns Not Applicable into Computed 5.
  "cloakDetection",
  "antimatter",
  "speed",
  "range",
  "dps",
  "novaDamage",
  "dpsPerSupply",
  "dpsPerCost",
  "costPerHp",
  "ehp",
  "bombingRate",
  "populationRate",
  "credits",
  "metal",
  "crystal",
] as const;
export type FoldedStat = (typeof FOLDED_STATS)[number];
export type FoldedUnitCells = Record<FoldedStat, StatCell>;

export interface FoldedUnit {
  cells: FoldedUnitCells;
  /** One entry per battery, in `unit.weapons` order. */
  weapons: FoldedWeaponCells[];
  /** The carrier's hangar under the active sources (Issue 48), absent on a hull with no
   * carrier block. A hangar Ship Item grows the shared capacity, so the craft counts are a
   * fold, not a stored fact — an Argonev reads 0 with nothing equipped and 14 with its three
   * Auxiliary Hangars on. */
  squadrons?: UnitSquadrons;
}

/** What the fold reads off a battery — the weapon file's own quantities, its mount count
 * and gating, and its tagged lists. A Pick so a test can hand in a bare battery. */
export type FoldableWeapon = Pick<
  UnitWeapon,
  | "id"
  | "weaponType"
  | "firingType"
  | "damage"
  | "cooldown"
  | "range"
  | "burstShots"
  | "bombingDamage"
  | "populationDamage"
  | "count"
  | "itemGated"
  | "researchGated"
  | "requiredSubjectId"
  | "modifiers"
>;

/** What the fold reads off a unit: its Base cells, its price facts, its batteries and its
 * tagged lists. */
export type FoldableUnit = Pick<
  Unit,
  | "id"
  | "squadrons"
  | "credits"
  | "metal"
  | "crystal"
  | "exoticPrice"
  | "supplyCost"
  | "hull"
  | "armor"
  | "shields"
  | "durability"
  | "armorStrength"
  | "cloakDetection"
  | "antimatter"
  | "speed"
  | "levels"
  | "modifiers"
> & {
  weapons: readonly FoldableWeapon[];
  /** The hull's abilities, for the levels its Level puts in force (Issue 51). Optional so a
   * test can fold a bare unit; a dataset Unit always carries the list. */
  abilities?: readonly FoldableAbility[];
};

/** The base row a Level puts under the modifier arithmetic (ADR 0012: a Level IS the base),
 * or undefined on a hull with no ladder — whose own cells are its only base, under every
 * level. An out-of-range level falls back to level 1 rather than throwing: the URL layer
 * reports a bad token, and a fold asked for a level the hull does not have folds nothing.
 *
 * This is the one step the fold gained that ADR 0009's tagged lists cannot express. */
function levelRow(unit: Pick<FoldableUnit, "levels">, active: ActiveSources) {
  const levels = unit.levels;
  if (levels === undefined) return undefined;
  return levels[activeLevel(active) - 1] ?? levels[0];
}

// Three policies decide what the fold does with a Base cell that is not Computed, and every
// stat takes exactly one of them. They are named because they are genuinely different answers
// to one question, and an unnamed one is how a fourth gets invented by accident.

/** **The base is required.** hull, armor, shields, the three Price cells and supplyCost are
 * Computed on every unit the dataset ships, and the cells that divide by them would print a
 * number rather than a state if one were not. So a non-Computed cell here is a broken
 * extraction invariant and it throws, naming the unit and the stat (CLAUDE.md: extraction
 * fails loudly rather than guessing). */
function requiredBase(unitId: string, what: string, cell: StatCell): number {
  if (cell.state !== "computed") {
    throw new Error(`fold: ${unitId} ${what} is ${cell.state}, not a Computed input`);
  }
  return cell.value;
}

/** **The base passes through.** A stat a unit simply does not have — Armor Strength with no
 * armor pool, antimatter with no pool, Speed on a structure — states a frame-invariant fact
 * (ADR 0003 Not Applicable), and nothing a reader ticks can give a structure a Speed. So the
 * unit's own cell is the answer under every selection.
 *
 * A non-Computed base with modifiers waiting on it is the one thing this must not swallow:
 * folding is impossible and passing the cell through would DROP bonuses the dataset says can
 * touch the stat. No unit reaches that at 2.0.3.0 — which is exactly why it throws instead of
 * dropping them quietly the day a patch makes it reachable. */
function passthroughBase(
  unitId: string,
  what: string,
  own: StatCell,
  base: number | undefined,
  modifiers: StatModifier[],
  verification: VerificationState,
): StatCell {
  if (base !== undefined) return foldStatCell(base, modifiers, verification);
  if (modifiers.length > 0) {
    throw new Error(
      `fold: ${unitId} ${what} is ${own.state}, yet ${modifiers.length} modifier(s) can touch it`,
    );
  }
  return own;
}

const optionalValue = (cell: StatCell): number | undefined =>
  cell.state === "computed" ? cell.value : undefined;

/** The cost input as the cells state it: a Not Applicable credits cell (hangar-built) is
 * the compute-side reason verbatim; otherwise the loaded price plus the loaded exotics'
 * fabrication.
 *
 * "Loaded" is the whole of Issue 47 (ADR 0012: item cost is part of the unit's cost). A
 * Loadout's items are bought with credits, metal, crystal and sometimes exotics, so their
 * prices join the hull's and the two cost-derived cells — DPS / cost and Cost / HP — measure
 * the whole investment. Without it a loaded ship divides item-boosted damage by hull-only
 * cost, which flatters every Loadout.
 *
 * A Not Applicable cost stays Not Applicable with its reason: a Loadout does not manufacture
 * a price for a hull the game never charges for. (No such hull has item slots today, so the
 * early return is a guarantee rather than a case — but it is the guarantee that matters.) */
function costInput(unit: FoldableUnit, active: ActiveSources): CostInput {
  if (unit.credits.state === "not_applicable") return { notApplicable: unit.credits.reason };
  const items = equippedItems(active);
  return {
    price: {
      credits: requiredBase(unit.id, "credits", unit.credits) + sumItemPrice(items, "credits"),
      metal: requiredBase(unit.id, "metal", unit.metal) + sumItemPrice(items, "metal"),
      crystal: requiredBase(unit.id, "crystal", unit.crystal) + sumItemPrice(items, "crystal"),
    },
    // The same function the hull's own exotics go through, so both halves of a Price are
    // measured the same way: each exotic at its race's fabrication recipe.
    exoticsFabrication: exoticFabricationTotal(loadedExoticPrice(unit.exoticPrice, active)),
  };
}

/** The Loadout as a list, in the map's own order; empty when nothing is equipped. */
function equippedItems(active: ActiveSources): readonly EquippedItem[] {
  return active.items === undefined ? [] : [...active.items.values()];
}

function sumItemPrice(items: readonly EquippedItem[], resource: keyof EquippedItem["price"]) {
  return items.reduce((total, item) => total + item.price[resource], 0);
}

/** The hull's exotic components plus every equipped item's, merged by exotic type so a
 * defense exotic on the hull and one on an item read as a count of two rather than two
 * entries. Exported because the entity page's Cost card lists exactly these and must not
 * total a different set from the one the efficiency cells divide by. */
export function loadedExoticPrice(
  own: readonly ExoticPrice[],
  active: ActiveSources,
): readonly ExoticPrice[] {
  const items = equippedItems(active);
  if (items.length === 0) return own;
  const merged: ExoticPrice[] = [];
  for (const price of [...own, ...items.flatMap((item) => item.exoticPrice)]) {
    const seen = merged.find((entry) => entry.exoticType === price.exoticType);
    if (seen === undefined) {
      merged.push({ ...price });
      continue;
    }
    // Two entries for one exotic type must agree on what the factory charges, or the merged
    // count would silently be priced at whichever recipe was seen first. A ship item is
    // race-scoped and only its own Race's hulls can buy it, so today they always agree —
    // this fails loudly if a patch ever crosses that line.
    if (
      seen.fabrication.credits !== price.fabrication.credits ||
      seen.fabrication.metal !== price.fabrication.metal ||
      seen.fabrication.crystal !== price.fabrication.crystal
    ) {
      throw new Error(
        `fold: two ${price.exoticType} exotic prices in one Loadout disagree on their ` +
          "fabrication recipe — a Loadout has crossed a Race boundary",
      );
    }
    seen.count += price.count;
  }
  return merged;
}

/** One Price cell under the Loadout: the hull's own figure with every equipped item's added.
 * A hull the game never charges for keeps its Not Applicable cell verbatim — a Loadout does
 * not manufacture a price — and an empty Loadout adds zero, so the cell is byte-identical to
 * the dataset's own. Exotics are NOT in here: they are counted in the derived cells at their
 * fabrication price and listed as components in their own right. */
function priceCell(
  own: StatCell,
  cost: CostInput,
  resource: "credits" | "metal" | "crystal",
): StatCell {
  if ("notApplicable" in cost) return own;
  // Through computedCell like every other published number, so the Price cells round at the
  // one rounding point and carry whatever Assumption Caption the hull's own figure carried.
  return computedCell(cost.price[resource], own.state === "computed" ? (own.captions ?? []) : []);
}

/** **An absent base is zero, and only the selection can create one.** The third policy, and
 * Cloak Detection is the one cell that takes it (Issue 55, ADR 0013) — the one folded cell
 * the selection can change the ADR 0003 STATE of, not just the number.
 *
 * A hull with no innate `detection_quality` reads Not Applicable, "has no cloak detection":
 * the game omits the row for such a unit and a unit at 0 sees through nothing, every cloak
 * in the game being quality 2 or better. Fitting the Eidolon Cloak Detector Array GIVES it
 * the stat, so the cell becomes Computed 5 — and that is the only way it moves in the whole
 * 2.0.3.0 dataset, because all six hulls the shop lets buy the item carry no innate
 * detection. With nothing active the hull's own cell passes through verbatim, captions and
 * all, which is what keeps the Base frame byte-identical to the dataset's stored fact.
 *
 * The TEC Detection Droids grants the same modifier type, +4, and never reaches this list:
 * it is an activated ability (120 s cooldown, 30 antimatter, 60 s of effect), and only a
 * bonus a ship holds permanently enters a stat row. So the Hoshiko Robotics Cruiser reads
 * "has no cloak detection" on a page that also shows an ability granting +4 — the row
 * describes the ship, the ability describes what it can do for a minute. */
function absentBaseIsZero(
  own: StatCell,
  modifiers: TaggedModifier[],
  verification: VerificationState,
): StatCell {
  if (modifiers.length === 0) return own;
  return foldStatCell(optionalValue(own) ?? 0, modifiers, verification);
}

function supplyInput(unit: FoldableUnit): SupplyInput {
  if (unit.supplyCost.state === "not_applicable") return { notApplicable: unit.supplyCost.reason };
  return { cost: requiredBase(unit.id, "supplyCost", unit.supplyCost) };
}

function weaponInput(weapon: FoldableWeapon, active: ActiveSources): WeaponInput {
  const lists = weapon.modifiers;
  return {
    id: weapon.id,
    weaponType: weapon.weaponType,
    ...(weapon.firingType === undefined ? {} : { firingType: weapon.firingType }),
    ...(weapon.damage === undefined ? {} : { damage: weapon.damage }),
    cooldown: weapon.cooldown,
    range: weapon.range,
    ...(weapon.burstShots === undefined ? {} : { burstShots: weapon.burstShots }),
    ...(weapon.bombingDamage === undefined ? {} : { bombingDamage: weapon.bombingDamage }),
    ...(weapon.populationDamage === undefined ? {} : { populationDamage: weapon.populationDamage }),
    researchModifiers: {
      damage: select(lists.damage, active),
      cooldown: select(lists.cooldown, active),
      range: select(lists.range, active),
      ...(lists.bombingDamage === undefined
        ? {}
        : { bombingDamage: select(lists.bombingDamage, active) }),
    },
  };
}

/** Whether a research-added mount exists under the selection: with every source (Fully-
 * Teched) it does; under a Tech Set exactly when the subject that adds it is ticked. A
 * mount whose gate the dataset does not name cannot be ticked, so a set leaves it out.
 * Exported because the weapon table's "needs research" note must say the same thing the
 * fold did: one rule, not a hand-written twin that can drift from it. */
export function mountUnlocked(weapon: FoldableWeapon, active: ActiveSources): boolean {
  if (weapon.researchGated !== true) return true;
  if (active.research === "all") return true;
  return weapon.requiredSubjectId !== undefined
    ? isSubjectActive(weapon.requiredSubjectId, active.research)
    : false;
}

/** One mount per counted battery slot; the research-gated flag stays on a mount the
 * selection has not unlocked (Base excludes it behind its caption). */
function mountsOf(unit: FoldableUnit, active: ActiveSources): Mount[] {
  return unit.weapons.flatMap((weapon) => {
    const mount: Mount = {
      weapon: weaponInput(weapon, active),
      itemGated: weapon.itemGated,
      ...(mountUnlocked(weapon, active) ? {} : { researchGated: true }),
    };
    return Array.from({ length: weapon.count }, () => mount);
  });
}

const CANNOT_TARGET_SHIPS: StatCell = {
  state: "not_applicable",
  reason: "a planet-bombing weapon cannot target ships",
};

/** A battery's cells under the active sources. A bomb bay's Damage, DPS and Nova Damage are
 * the stated fact that it cannot target ships under every selection; its Cooldown, Range and
 * planet damage fold like any other quantity. */
export function foldWeapon(
  weapon: FoldableWeapon,
  active: ActiveSources,
  verification: VerificationState,
): FoldedWeaponCells {
  const input = weaponInput(weapon, active);
  const bombing = weapon.weaponType === "planet_bombing";
  return {
    damage: bombing ? CANNOT_TARGET_SHIPS : weaponDamage(input, verification),
    cooldown: weaponCooldown(input, verification),
    dps: bombing ? CANNOT_TARGET_SHIPS : weaponDps(input, verification),
    novaDamage: bombing ? CANNOT_TARGET_SHIPS : weaponNovaDamage(input, verification),
    range: weaponRange(input, verification),
    ...(bombing ? { bombingDamage: weaponBombingDamage(input, verification) } : {}),
  };
}

/** The Provides lines a reader sees under the active sources (Issue 48, ADR 0012). A line
 * gated on a Ship Item (`requiredItems`) is stated exactly when the Loadout holds one of
 * them; every other line is unconditional and passes straight through.
 *
 * This is the only place the gate is decided, so the entity page, the comparison and the two
 * roster tables cannot disagree about whether a hull can build ships — which is the failure
 * issue 43 was fixing when it withdrew these lines wholesale. The roster views declare no
 * `?items=` at all, so they fold under an empty Loadout and show exactly the unconditional
 * lines, which is what a list of bare hulls should say.
 *
 * Research does NOT open one of these: an item is bought and fitted, not researched, so
 * Fully-Teched equips nothing (ADR 0012). */
export function activeProvides(
  lines: readonly ProvidesLine[],
  active: ActiveSources,
): ProvidesLine[] {
  return lines.filter(
    (line) =>
      line.requiredItems === undefined ||
      line.requiredItems.some((id) => active.items?.has(id) === true),
  );
}

/** What the Cloaking cell reads off a unit: its `cloak` block, if it has one, and the
 * abilities that block's ability may be among. A `Pick` so a test can hand in a bare hull. */
export interface CloakableUnit {
  cloak?: UnitCloak;
  abilities?: readonly FoldableAbility[];
}

/** The reason the Cloaking cell is Not Applicable (ADR 0013, CONTEXT.md: Cloaking). One
 * reason covers three cases — a hull with no cloak at all, one whose Research Subject is
 * unticked, one whose granting item is unfitted — because they are the same fact about the
 * unit in front of the reader: it cannot cloak. Deliberately NOT the game's "Not Cloaked",
 * which is a live status a cloak-capable ship also shows while uncloaked, and a static
 * reference has no live status. */
export const CANNOT_CLOAK = "cannot cloak";

/** The Assumption Caption every Cloaking cell carries (ADR 0013 decision 3). Cloak quality
 * is not itself a rung: it becomes one only after an enemy's Cloak Detection is subtracted,
 * so naming a rung at all is an assumption — the same shape as EHP's "vs 0 Pierce". */
export const CLOAK_NO_DETECTION_CAPTION = "against an enemy with no cloak detection";

/** The top rung the game names. A quality above it is not a ladder violation but a buffer:
 * the Anathema's cloaking crest is 5, so it absorbs one degrade for free (ADR 0013). */
export const CLOAK_LADDER_TOP = 4;

/** The second caption a quality above the ladder carries — ours, and stated as ours until
 * the reading in `.scratch/sins2-atlas/in-game-protocol-cloak.md` Q4 confirms it. */
export const CLOAK_ABOVE_LADDER_CAPTION = `the game names rungs 0 to ${CLOAK_LADDER_TOP}, so a higher quality is shown at the top rung`;

/** The cloak quality this hull reaches under the reader's selection, or undefined where it
 * cannot cloak at all (ADR 0013 decision 2).
 *
 * Three ways to reach undefined, and they are the whole of what makes this the first cell
 * whose ADR 0003 state the selection decides: the hull has no cloak; the hull's cloak
 * ability is one no level is in force for (a Research Subject the Tech Set does not hold);
 * or the ability is not among the hull's own — a Ship Item grants it and no Loadout here has
 * fitted it. The level in force is `abilityLevelStateUnder`'s, the same rule the ability list
 * marks with, so what the row states and what the ability list says can never disagree. */
export function cloakQualityUnder(unit: CloakableUnit, active: ActiveSources): number | undefined {
  const cloak = unit.cloak;
  if (cloak === undefined) return undefined;
  const ability = (unit.abilities ?? []).find((candidate) => candidate.id === cloak.abilityId);
  // The item route (Issue 58): the ability is not on the hull at all — a Ship Item grants it,
  // so it exists exactly while the Loadout holds that item. An item's own level is decided by
  // research (ADR 0012), and it is the item's level that picks the ability level, so a fitted
  // crest reads its rung off the same one-entry-per-level ladder every other cloak does.
  if (ability === undefined) return itemGrantedCloakQuality(cloak, active);
  const state = abilityLevelStateUnder(ability, active);
  // `not_level_driven` is level 1, not "off": it means neither lever decides this ability, so
  // the hull simply has it (an item-granted cloak, or one the game gives unconditionally).
  // The two locked states and `unspent` are the ones that withdraw the cell —
  // `research_locked` is what the two Vasari corvettes read until their subject is ticked.
  // No cloak ability is level-driven at 2.0.3.0, so `unspent` cannot arise here today.
  const level =
    state.state === "in_force" ? state.level : state.state === "not_level_driven" ? 1 : undefined;
  if (level === undefined) return undefined;
  // Exactly one level's entry, never a sum of the rungs below it — the same rule a Ship
  // Item's per-level values and a Level's own rows follow. Extraction pins one entry per
  // ability level, so a miss here is a dataset that has drifted from its own invariant.
  return cloak.quality[level - 1];
}

/** The cloak quality a Loadout gives a hull whose cloak ability it does not carry itself
 * (Issue 58, ADR 0012 + ADR 0013). One hull takes this route at 2.0.3.0 — the Zhaltharien
 * Anathema, whose Cloak Generator crest grants `dlc3_herald_titan_crest_cloaking` and reaches
 * quality 5, the site's highest.
 *
 * The Thalkerin Revenant is the other half of the story and is not reachable here: its Cloak
 * Generator is in no faction's shop, so the item table never ships it, the hull publishes no
 * cloak block at all, and this function is never asked (ADR 0013's amendment).
 *
 * The level in force is the ITEM's, not the hull's — an item's level comes from research
 * alone (ADR 0012), so the Tech Set decides how strong a fitted crest is and there is no
 * fourth axis here either. */
function itemGrantedCloakQuality(cloak: UnitCloak, active: ActiveSources): number | undefined {
  for (const item of active.items?.values() ?? []) {
    if (item.grantedAbilityId !== cloak.abilityId) continue;
    return cloak.quality[item.level - 1];
  }
  return undefined;
}

/** One hull's Cloaking cell under the reader's selection (ADR 0013). */
export function cloakingCell(unit: CloakableUnit, active: ActiveSources): StatCell {
  const quality = cloakQualityUnder(unit, active);
  if (quality === undefined) return { state: "not_applicable", reason: CANNOT_CLOAK };
  return {
    state: "computed",
    value: quality,
    captions:
      quality > CLOAK_LADDER_TOP
        ? [CLOAK_NO_DETECTION_CAPTION, CLOAK_ABOVE_LADDER_CAPTION]
        : [CLOAK_NO_DETECTION_CAPTION],
  };
}

/** The carrier's hangar under the active sources: the shared capacity folded from the unit's
 * `squadronCapacity` list, then each craft option's own maximum re-derived from it.
 *
 * Every option is an ALTERNATIVE, not an addition — the slots are shared — so `maxCraft` is
 * the most of THAT craft the hull could field if it filled every slot with it, honouring the
 * per-kind cap where the file declares one. `maxSquadrons` states that cap only while it
 * binds, which a Loadout can change: an uncapped kind's cap is the files' own "unlimited"
 * 99, and the shared capacity binds long before it.
 *
 * The capacity arithmetic is the shared composition rule (base + Σadditive) × (1 + Σscalar)
 * — the same foldStat every stat cell goes through. It publishes no cell of its own because
 * the game states a hangar as a count of slots, not as a stat with an ADR 0003 state. */
function foldSquadrons(
  squadrons: UnitSquadrons,
  modifiers: readonly TaggedModifier[],
  active: ActiveSources,
  /** The Level's own capacity for this hull (Issue 49), replacing the block's Base figure.
   * Absent on a hull with no ladder, where the stored capacity IS the base. */
  baseCapacity?: number,
): UnitSquadrons {
  const capacity = foldStat(baseCapacity ?? squadrons.capacity, select(modifiers, active));
  const options = squadrons.options.map((option): SquadronOption => {
    const cap = option.kindCapacity;
    const filled = cap === undefined ? capacity : Math.min(capacity, cap);
    return {
      kind: option.kind,
      unitId: option.unitId,
      name: option.name,
      squadronSize: option.squadronSize,
      maxCraft: filled * option.squadronSize,
      ...(cap !== undefined && cap < capacity ? { maxSquadrons: cap } : {}),
      ...(cap === undefined ? {} : { kindCapacity: cap }),
      ...(option.researchGated === true ? { researchGated: true as const } : {}),
    };
  });
  const [first, ...rest] = options;
  if (first === undefined) {
    throw new Error("fold: a carrier's squadron block has no craft options");
  }
  return { capacity, options: [first, ...rest] };
}

/** Every cell the surfaces render for one unit under the active sources: its own folded
 * stats, the derived stats over its counted mounts, EHP, and each battery's cells. A stat
 * the unit lacks (no armor pool, cannot move) keeps its Base fact under every selection —
 * nothing can be ticked into giving a structure a Speed. */
export function foldUnit(
  unit: FoldableUnit,
  activeSources: ActiveSources,
  verification: VerificationState,
): FoldedUnit {
  // Which of the hull's abilities are in force, and at which Ability Level, is a function of
  // the hull, its Level (the gates) and the allocation (what was bought — ADR 0015), so it is
  // resolved here rather than asked of every caller. Doing it once is what stops the roster
  // and the entity page disagreeing about a passive (Issue 51).
  const active = withAbilityLevels(activeSources, unit.abilities);
  // A Level is not a modifier: it REPLACES the base the modifiers then apply to (ADR 0012,
  // observed in game 2026-09-06 — a Kol's researched hull is a constant multiple of its LEVEL
  // row, not of level 1's). So the row is chosen first, and everything below is unchanged.
  const row = levelRow(unit, active);
  const hull = row?.hull ?? requiredBase(unit.id, "hull", unit.hull);
  const armor = row?.armor ?? requiredBase(unit.id, "armor", unit.armor);
  const shields = row?.shields ?? requiredBase(unit.id, "shields", unit.shields);
  const durability = optionalValue(unit.durability);
  const armorStrength = row?.armorStrength ?? optionalValue(unit.armorStrength);
  const antimatter = row?.antimatter ?? optionalValue(unit.antimatter);
  const speed = optionalValue(unit.speed);
  const unitMods = {
    hull: select(unit.modifiers.hull, active),
    armor: select(unit.modifiers.armor, active),
    shields: select(unit.modifiers.shields, active),
    speed: select(unit.modifiers.speed, active),
    armorStrength: select(unit.modifiers.armorStrength, active),
  };
  const cost = costInput(unit, active);
  const derived = deriveUnitStats(
    mountsOf(unit, active),
    {
      cost,
      supply: supplyInput(unit),
      hull,
      armor,
      shields,
      researchModifiers: unitMods,
    },
    verification,
  );
  return {
    cells: {
      hull: foldStatCell(hull, unitMods.hull, verification),
      armor: foldStatCell(armor, unitMods.armor, verification),
      shields: foldStatCell(shields, unitMods.shields, verification),
      armorStrength: passthroughBase(
        unit.id,
        "armorStrength",
        unit.armorStrength,
        armorStrength,
        unitMods.armorStrength,
        verification,
      ),
      cloakDetection: absentBaseIsZero(
        unit.cloakDetection,
        select(unit.modifiers.cloakDetection, active),
        verification,
      ),
      antimatter: passthroughBase(
        unit.id,
        "antimatter",
        unit.antimatter,
        antimatter,
        select(unit.modifiers.antimatter, active),
        verification,
      ),
      speed: passthroughBase(unit.id, "speed", unit.speed, speed, unitMods.speed, verification),
      range: derived.range,
      dps: derived.dps,
      novaDamage: derived.novaDamage,
      dpsPerSupply: derived.dpsPerSupply,
      dpsPerCost: derived.dpsPerCost,
      costPerHp: derived.costPerHp,
      ehp: effectiveHp(
        {
          hull,
          armor,
          shields,
          ...(durability === undefined ? {} : { durability }),
          ...(armorStrength === undefined ? {} : { armorStrength }),
          researchModifiers: unitMods,
        },
        verification,
      ),
      bombingRate: derived.bombingRate,
      populationRate: derived.populationRate,
      // The Price the two cost cells above just divided by, published as its own three
      // cells — one source of truth per surface, so what the reader sees added up is what
      // the efficiency stats divided by.
      credits: priceCell(unit.credits, cost, "credits"),
      metal: priceCell(unit.metal, cost, "metal"),
      crystal: priceCell(unit.crystal, cost, "crystal"),
    },
    weapons: unit.weapons.map((weapon) => foldWeapon(weapon, active, verification)),
    ...(unit.squadrons === undefined
      ? {}
      : {
          squadrons: foldSquadrons(
            unit.squadrons,
            unit.modifiers.squadronCapacity,
            active,
            // The hangar's shared capacity is a per-level base too (Issue 49): a Kol Carrier
            // fields 2 squadrons at level 1 and 7 at level 10 before an item touches it.
            row?.squadronCapacity,
          ),
        }),
  };
}
